// Resolve the user-facing invoice number from an invoice's internal `invoiceid`.
//
// Invoices carry both an internal global `invoiceid` (the DB primary key) and a
// per-tenant, user-facing `invoice_number`. Child rows (billed time, expenses,
// fixed fees, other billing) only store the FK `invoiceid`, so to display the
// number a user recognizes we map that id to the invoice's `invoice_number`.

type InvoiceLike = {
  invoiceid?: unknown;
  invoice_number?: unknown;
};

export type InvoiceNumberMap = Map<string, number | string>;

/** Build an `invoiceid -> invoice_number` lookup from a list of invoices. */
export function buildInvoiceNumberMap(invoices: InvoiceLike[] | null | undefined): InvoiceNumberMap {
  const map: InvoiceNumberMap = new Map();
  if (!Array.isArray(invoices)) return map;
  for (const inv of invoices) {
    if (!inv || inv.invoiceid == null) continue;
    const key = String(inv.invoiceid);
    const num = inv.invoice_number;
    if (num != null && num !== '') {
      map.set(key, num as number | string);
    }
  }
  return map;
}

/**
 * Resolve the display number for a child row's `invoiceid`.
 * Falls back to the raw id when no mapping exists (e.g. backfilled rows where
 * invoice_number equals invoiceid, or an invoice not present in the map).
 */
export function resolveInvoiceNumber(
  invoiceid: unknown,
  map: InvoiceNumberMap,
): number | string | null {
  if (invoiceid == null || invoiceid === '') return null;
  return map.get(String(invoiceid)) ?? (invoiceid as number | string);
}
